import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Truck, ShieldCheck, RotateCcw, Send, Building2 } from 'lucide-react';
import { CONTACT_INFO, categories, CARD_DESCRIPTOR } from '../../data/mock';

const PaymentLogos = () => (
  <div className="flex flex-wrap items-center gap-2">
    <div className="h-7 w-12 bg-white rounded-md border border-dark-200 flex items-center justify-center px-1.5" title="Visa">
      <svg viewBox="0 0 48 32" className="h-5 w-auto" aria-label="Visa"><text x="24" y="22" textAnchor="middle" fontSize="14" fontWeight="900" fill="#1A1F71" fontFamily="Arial, sans-serif" fontStyle="italic">VISA</text></svg>
    </div>
    <div className="h-7 w-12 bg-white rounded-md border border-dark-200 flex items-center justify-center" title="Mastercard">
      <svg viewBox="0 0 48 32" className="h-5 w-auto" aria-label="Mastercard"><circle cx="19" cy="16" r="9" fill="#EB001B" /><circle cx="29" cy="16" r="9" fill="#F79E1B" /><path d="M24 9.5 a9 9 0 0 0 0 13" fill="#FF5F00" /></svg>
    </div>
    <div className="h-7 w-12 bg-[#2E77BB] rounded-md flex items-center justify-center px-1" title="American Express">
      <svg viewBox="0 0 48 32" className="h-5 w-auto" aria-label="American Express"><rect x="0" y="0" width="48" height="32" fill="#2E77BB" /><text x="24" y="14" textAnchor="middle" fontSize="6.5" fontWeight="900" fill="#FFFFFF" fontFamily="Arial, sans-serif" letterSpacing="0.6">AMERICAN</text><text x="24" y="22" textAnchor="middle" fontSize="6.5" fontWeight="900" fill="#FFFFFF" fontFamily="Arial, sans-serif" letterSpacing="0.6">EXPRESS</text><text x="24" y="30" textAnchor="middle" fontSize="5" fontWeight="800" fill="#FFFFFF" fontFamily="Arial, sans-serif" letterSpacing="1.2">AMEX</text></svg>
    </div>
    <div className="h-7 w-12 bg-white rounded-md border border-dark-200 flex items-center justify-center px-1" title="Discover">
      <svg viewBox="0 0 48 32" className="h-5 w-auto" aria-label="Discover"><text x="22" y="21" textAnchor="middle" fontSize="8" fontWeight="800" fill="#231F20" fontFamily="Arial, sans-serif">DISC</text><circle cx="40" cy="18" r="4" fill="#F76B1C" /></svg>
    </div>
  </div>
);

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const trust = [
    { icon: Truck, title: 'Free USA Shipping', text: 'USPS on every order' },
    { icon: RotateCcw, title: '30-Day Returns', text: 'Simple, no-hassle' },
    { icon: ShieldCheck, title: 'Secure Checkout', text: 'Encrypted payments' },
  ];

  return (
    <footer className="mt-16 bg-dark-50 border-t border-dark-200">
      {/* Trust badges strip */}
      <div className="border-b border-dark-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 grid grid-cols-1 sm:grid-cols-3 gap-6">
          {trust.map((t) => (
            <div key={t.title} className="flex items-center gap-3 justify-center sm:justify-start">
              <div className="w-11 h-11 rounded-lg bg-athletic-100 text-athletic-700 flex items-center justify-center"><t.icon className="w-5 h-5" /></div>
              <div><p className="text-dark-900 font-semibold text-sm">{t.title}</p><p className="text-dark-500 text-xs">{t.text}</p></div>
            </div>
          ))}
        </div>
      </div>

      {/* Main columns + newsletter */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 grid grid-cols-2 lg:grid-cols-12 gap-10">
        <div className="col-span-2 lg:col-span-4">
          <Link to="/" className="inline-flex items-center gap-3 mb-4">
            <span className="w-10 h-10 rounded-xl bg-gradient-to-br from-athletic-500 to-athletic-700 flex items-center justify-center font-display font-semibold text-white text-base">ON</span>
            <span className="font-display text-2xl font-semibold text-dark-900">Office <span className="text-athletic-600">Nest</span> Solutions</span>
          </Link>
          <p className="text-dark-500 text-sm leading-relaxed mb-5 max-w-sm">Premium office supplies and workspace essentials for focused, organized workdays &mdash; from desk organizers and writing sets to ergonomic furniture, shipped fast and free across the USA.</p>
          <div className="space-y-2 text-sm">
            <a href={`tel:${CONTACT_INFO.phone.replace(/[^0-9]/g, '')}`} className="flex items-start gap-2 text-dark-600 hover:text-athletic-600 transition-colors"><Phone className="w-4 h-4 mt-0.5 text-athletic-600" /> <span>{CONTACT_INFO.phone}</span></a>
            <a href={`mailto:${CONTACT_INFO.email}`} className="flex items-start gap-2 text-dark-600 hover:text-athletic-600 transition-colors"><Mail className="w-4 h-4 mt-0.5 text-athletic-600" /> <span className="break-all">{CONTACT_INFO.email}</span></a>
            <div className="flex items-start gap-2 text-dark-600"><MapPin className="w-4 h-4 mt-0.5 text-athletic-600 flex-shrink-0" /><span>{CONTACT_INFO.address.street}<br />{CONTACT_INFO.address.city}, {CONTACT_INFO.address.state} {CONTACT_INFO.address.zip}</span></div>
          </div>
        </div>

        <div className="lg:col-span-2">
          <h4 className="text-dark-900 font-bold text-sm uppercase tracking-wider mb-4">Shop</h4>
          <ul className="space-y-2.5">
            <li><Link to="/shop" className="text-dark-500 hover:text-athletic-600 text-sm transition-colors">All Products</Link></li>
            {categories.map((cat) => (<li key={cat.id}><Link to={`/shop?category=${cat.slug}`} className="text-dark-500 hover:text-athletic-600 text-sm transition-colors">{cat.name}</Link></li>))}
          </ul>
        </div>

        <div className="lg:col-span-2">
          <h4 className="text-dark-900 font-bold text-sm uppercase tracking-wider mb-4">Support</h4>
          <ul className="space-y-2.5">
            {[{ name: 'Shipping', path: '/shipping' }, { name: 'Returns', path: '/returns' }, { name: 'FAQ', path: '/faq' }, { name: 'Our Mission', path: '/our-mission' }, { name: 'Why Choose Us', path: '/why-choose-us' }].map((link) => (<li key={link.path}><Link to={link.path} className="text-dark-500 hover:text-athletic-600 text-sm transition-colors">{link.name}</Link></li>))}
          </ul>
        </div>

        {/* Newsletter card */}
        <div className="col-span-2 lg:col-span-4">
          <div className="bg-white rounded-2xl border border-dark-100 shadow-card p-6">
            <h4 className="font-display text-xl font-semibold text-dark-900">Stay stocked & organized</h4>
            <p className="text-dark-500 text-sm mt-1 mb-4">Restock reminders, new arrivals and subscriber-only savings &mdash; straight to your inbox.</p>
            <form className="flex gap-2" onSubmit={(e) => e.preventDefault()}>
              <input type="email" placeholder="Your email address" className="flex-1 px-4 py-3 rounded-lg text-dark-900 text-sm bg-dark-50 border border-dark-200 placeholder-dark-400 focus:outline-none focus:ring-2 focus:ring-athletic-500" />
              <button type="submit" className="px-5 py-3 rounded-lg bg-athletic-600 text-white font-semibold text-sm flex items-center gap-2 hover:bg-athletic-700 transition-colors"><Send className="w-4 h-4" /></button>
            </form>
            <ul className="mt-5 space-y-2 text-sm">
              <li><Link to="/about" className="text-dark-500 hover:text-athletic-600 transition-colors">Our Story</Link></li>
              <li><Link to="/quality-guarantee" className="text-dark-500 hover:text-athletic-600 transition-colors">Quality Guarantee</Link></li>
              <li><Link to="/contact" className="text-dark-500 hover:text-athletic-600 transition-colors">Contact Us</Link></li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="bg-dark-900 text-dark-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-3 text-sm text-dark-400">
            <span>&copy; {currentYear} Office Nest Solutions</span>
            <span className="hidden sm:block text-white/15">|</span>
            <span className="flex items-center gap-1.5"><Building2 className="w-3.5 h-3.5 text-athletic-400" /> {CONTACT_INFO.corpName}</span>
          </div>
          <div className="flex items-center gap-3"><span className="text-dark-500 text-xs font-medium">We Accept</span><PaymentLogos /></div>
          <p className="text-xs text-dark-500">Charges show as &quot;<span className="font-semibold text-dark-200">{CARD_DESCRIPTOR}</span>&quot;</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
